import type { Invoice, Job } from "@clevis/shared-types";

// Pipeline buckets: Scheduled → … → Paid, DERIVED from what's true about a
// job, its child visits and its linked invoices — never stored, so nothing
// drifts and nobody has to shepherd a status field. Computed client-side on
// live snapshots (fine at current tenant scale; a server rollup can replace
// this transparently later).

export type JobRow = Job & { id: string };
export type InvRow = Invoice & { id: string };

export const BUCKETS = [
  "scheduled",
  "in_progress",
  "partially_complete",
  "waiting_parts",
  "warranty",
  "done_unbilled",
  "billed",
  "partially_paid",
  "paid",
] as const;
export type Bucket = (typeof BUCKETS)[number];

export const BUCKET_LABEL: Record<Bucket, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  partially_complete: "Partially complete",
  waiting_parts: "Waiting on parts",
  warranty: "Warranty",
  done_unbilled: "Done — not billed",
  billed: "Billed",
  partially_paid: "Partially paid",
  paid: "Paid",
};

export const BUCKET_HINT: Record<Bucket, string> = {
  scheduled: "On the calendar, nobody started yet",
  in_progress: "Someone is on it right now",
  partially_complete: "Some visits done, more to go",
  waiting_parts: "Material on order — not a scheduling failure",
  warranty: "Open warranty claims",
  done_unbilled: "Work finished — no invoice yet",
  billed: "Invoiced, nothing collected",
  partially_paid: "Progress payments coming in",
  paid: "Collected in full",
};

export interface PipelineJob {
  job: JobRow;
  children: JobRow[];
  invoices: InvRow[];
  bucket: Bucket;
}

export function computePipeline(
  jobs: JobRow[],
  invoices: InvRow[],
  /** Job ids with at least one part still on order (collection-group query). */
  openPartsJobIds: Set<string> = new Set(),
): Map<Bucket, PipelineJob[]> {
  const childrenOf = new Map<string, JobRow[]>();
  for (const j of jobs) {
    if (j.parentJobId) {
      const list = childrenOf.get(j.parentJobId) ?? [];
      list.push(j);
      childrenOf.set(j.parentJobId, list);
    }
  }
  const invoicesOf = new Map<string, InvRow[]>();
  for (const inv of invoices) {
    if (inv.jobId && inv.status !== "void") {
      const list = invoicesOf.get(inv.jobId) ?? [];
      list.push(inv);
      invoicesOf.set(inv.jobId, list);
    }
  }

  const buckets = new Map<Bucket, PipelineJob[]>(BUCKETS.map((b) => [b, []]));
  for (const job of jobs) {
    if (job.parentJobId) continue; // children ride with their parent
    if (job.status === "canceled") continue;
    const children = childrenOf.get(job.id) ?? [];
    const invs = invoicesOf.get(job.id) ?? [];
    const waitingParts = openPartsJobIds.has(job.id) || children.some((c) => openPartsJobIds.has(c.id));
    const bucket = bucketOf(job, children, invs, waitingParts);
    buckets.get(bucket)!.push({ job, children, invoices: invs, bucket });
  }
  // Oldest first inside each bucket — the thing that's been stuck longest on top.
  for (const list of buckets.values()) {
    list.sort((a, b) => (a.job.startAt ?? a.job.createdAt) - (b.job.startAt ?? b.job.createdAt));
  }
  return buckets;
}

function bucketOf(job: JobRow, children: JobRow[], invoices: InvRow[], waitingParts: boolean): Bucket {
  // An open warranty claim is its own lane, whoever ends up paying.
  if (job.kind === "warranty" && job.status !== "done") return "warranty";

  const live = children.filter((c) => c.status !== "canceled");
  const openChildren = live.filter((c) => c.status !== "done");
  const doneChildren = live.filter((c) => c.status === "done");
  const jobDone = job.status === "done";

  const workComplete = jobDone && openChildren.length === 0;
  // Material on order parks the job out of the scheduling lanes.
  if (!workComplete && waitingParts) return "waiting_parts";
  if (!workComplete) {
    // Work remains somewhere. Started anywhere → at least in progress.
    if (openChildren.length > 0 && (jobDone || doneChildren.length > 0)) return "partially_complete";
    if (job.status === "in_progress" || live.some((c) => c.status === "in_progress")) return "in_progress";
    return "scheduled";
  }

  // All work done — follow the money.
  if (invoices.length === 0) return "done_unbilled";
  // A written-off invoice is settled: what's left won't be collected.
  const total = invoices.reduce((s, i) => s + (i.status === "written_off" ? i.amountPaidCents : i.totalCents), 0);
  const paid = invoices.reduce((s, i) => s + i.amountPaidCents, 0);
  if (total === 0 && invoices.every((i) => i.status === "written_off")) return "paid";
  if (paid <= 0) return "billed";
  if (paid < total) return "partially_paid";
  return "paid";
}
